// const _ = (window as any)._;
import * as _ from 'lodash';
type List<T> = ArrayLike<T>;

interface Dictionary<T> {
    [index: string]: T;
}

interface NumericDictionary<T> {
    [index: number]: T;
}

interface RecursiveArray<T> extends Array<T | RecursiveArray<T>> {}
interface ListOfRecursiveArraysOrValues<T> extends List<T | RecursiveArray<T>> {}

type PartialDeep<T> = { [P in keyof T]?: PartialDeep<T[P]> };

type NotVoid = {} | null | undefined;

type ListIterator<T, TResult> = (value: T, index: number, collection: List<T>) => TResult;

type ListIterateeCustom<T, TResult> = ListIterator<T, TResult> | string | object | [string, any] | PartialDeep<T>;

type ListIteratee<T> = ListIterator<T, NotVoid> | string | [string, any] | PartialDeep<T>;

type ObjectIterator<TObject, TResult> = (value: TObject[keyof TObject], key: string, collection: TObject) => TResult;

type ValueIteratee<T> = ((value: T) => NotVoid) | string | [string, any] | PartialDeep<T>;

type Comparator2<T1, T2> = (a: T1, b: T2) => boolean;

type Comparator<T> = (a: T, b: T) => boolean;

type Many<T> = T | T[];

type PropertyName = string | number | symbol;

type PropertyPath = Many<PropertyName>;

type ObjectIterateeCustom<TObject, TResult> =
    | ObjectIterator<TObject, TResult>
    | string
    | [string, any]
    | PartialDeep<TObject[keyof TObject]>;

export function isEmpty(value: any) {
    return _.isEmpty(value);
}

export function isUndefined(value: any) {
    return _.isUndefined(value);
}

export function isNull(value: any) {
    return _.isNull(value);
}

export function isNullOrUndefined(value: any): boolean {
    return isUndefined(value) || isNull(value);
}

export function isFunction(value: any) {
    return _.isFunction(value);
}

export function isObject(value: any) {
    return _.isObject(value);
}

export function cloneDeep(value: any) {
    return _.cloneDeep(value);
}

export function clone(value: any) {
    return _.clone(value);
}

export function isNumber(value: any) {
    return _.isNumber(value);
}

export function isString(value: any) {
    return _.isString(value);
}

export function isArray(value: any) {
    return _.isArray(value);
}

// export function keyBy(collection: any, iteratee: any): any {
//     return (_ as any).indexBy(collection, iteratee);
// }

export function keyBy<T>(collection: List<T> | null | undefined, iteratee: any): Dictionary<T> {
    return (_ as any).indexBy(collection, iteratee);
}

export function get<T>(object: any, path: PropertyPath, defaultValue?: T): T {
    return _.get(object, path);
}

export function set<T extends object>(object: T, path: PropertyPath, value: any): T {
    return _.set(object, path, value);
}

export function findKey(object: any, predicate: any) {
    return _.findKey(object, predicate);
}

export function findIndex(collection: any, predicate: any) {
    return _.findIndex(collection, predicate);
}

export function includes(collection: any, predicate: any) {
    return _.includes(collection, predicate);
}

export function find<T>(collection: List<T> | null | undefined, predicate: ListIterateeCustom<T, boolean>, fromIndex?: number): T;
export function find<T, S extends T>(collection: List<T> | null | undefined, predicate: ListIterateeCustom<T, S>, fromIndex?: number): T {
    return _.find(collection, predicate, fromIndex);
}

export function filter(collection: any, predicate?: any) {
    return _.filter(collection, predicate);
}

export function some<T>(collection: List<T> | null | undefined, predicate?: ListIterateeCustom<T, boolean>): boolean;
export function some<T extends object>(collection: T | null | undefined, predicate?: ObjectIterateeCustom<T, boolean>): boolean;
export function some(collection: any, predicate?: any): boolean {
    return _.some(collection, predicate);
}

export function remove(object: any, predicate: any) {
    return _.remove(object, predicate);
}

export function forEach(collection: any, iteratee: any) {
    return _.forEach(collection, iteratee);
}

export function map(collection: any, iteratee: any) {
    return _.map(collection, iteratee);
}

export function flatten(collection: any, isDeep?: any) {
    return _.flatten(collection, isDeep);
}

export function merge(collection: any, iteratee: any) {
    return _.merge(collection, iteratee);
}

// export function assign(collection: any, iteratee: any) {
//     return _.assign(collection, iteratee);
// }

export function assign(object: any, ...otherArgs: any[]): any {
    return _.assign(object, ...otherArgs);
}

export function zipObject(collection: any) {
    return _.zipObject(collection);
}

export function chunk(array, size) {
    return _.chunk(array, size);
}

export function sortBy<T>(collection: List<T> | null | undefined, iteratee: ListIteratee<T>): T[] {
    return _.sortBy(collection, iteratee);
}

export function orderBy<T>(collection: List<T> | null | undefined, iteratee: ListIteratee<T> | string[], orders?: string[]): T[] {
    return _.sortByOrder(collection, iteratee, orders);
}

export function splitTwo(array: any[], firstSize: number) {
    const firstArray = [];
    const twoArray = [];
    array.forEach((item, index) => {
        if (index < firstSize) {
            firstArray.push(item);
        } else {
            twoArray.push(item);
        }
    });
    return [firstArray, twoArray];
}

export function union(...arrays) {
    return _.union(arrays);
}

export function uniq<T>(collection: List<T> | null | undefined): T[] {
    return (_ as any).uniq(collection);
}

export function uniqBy<T>(collection: List<T> | null | undefined, iteratee: ListIteratee<T>): T[] {
    return (_ as any).uniq(collection, iteratee);
}

export function sumBy<T>(collection: List<T> | null | undefined, iteratee: ListIteratee<T>): number {
    return (_ as any).sum(collection, iteratee);
}

export function pick(object: any, path: string | string[]) {
    return _.pick(object, path);
}

export function take<T>(collection: List<T> | null | undefined, size: number): T[] {
    return (_ as any).take(collection, size);
}

export function takeRight<T>(collection: List<T> | null | undefined, size: number): T[] {
    return (_ as any).takeRight(collection, size);
}

export function groupBy<T>(collection: List<T> | null | undefined, iteratee: ListIteratee<T>): Dictionary<T[]> {
    return _.groupBy(collection, iteratee);
}

export function round<T>(number: number, precision?: number): number {
    return _.round(number, precision);
}

export function difference<T>(array: List<T>, compareArray: List<T>) {
    return _.difference(array, compareArray);
}

export function every(collection: any, predicate?: any) {
    return _.every(collection, predicate);
}
